import { Box, IconButton, Stack, Typography } from '@mui/material';

import { currencyExact, currencyShort } from './format';
import { InvoicingSummary } from './types';

import NavigationSettingsIcon from 'modules/common/components/Icons/NavigationSettingsIcon';

function StackedBar({ summary }: { summary: InvoicingSummary }) {
    const total = summary.paid_count + summary.unpaid_count + summary.overdue_count;
    const safeTotal = total > 0 ? total : 1;
    const segments = [
        { count: summary.paid_count, color: 'success.main' },
        { count: summary.unpaid_count, color: 'cement.300' },
        { count: summary.overdue_count, color: 'warning.main' },
    ];
    return (
        <Box
            sx={{
                width: '100%',
                height: 12,
                bgcolor: 'grey.100',
                borderRadius: 999,
                display: 'flex',
                overflow: 'hidden',
                mt: 3,
                mb: 2,
            }}>
            {segments.map((s) =>
                s.count > 0 ? (
                    <Box
                        key={s.color}
                        sx={{ width: `${(s.count / safeTotal) * 100}%`, bgcolor: s.color, height: '100%' }}
                    />
                ) : null
            )}
        </Box>
    );
}

function LegendDot({ color, label }: { color: string; label: string }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Stack>
    );
}

export default function SummaryCard({ summary }: { summary: InvoicingSummary }) {
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', p: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h1">Invoicing Overview</Typography>
                <IconButton size="small" aria-label="Settings">
                    <NavigationSettingsIcon fontSize="small" />
                </IconButton>
            </Stack>
            <Stack direction="row" spacing={4} alignItems="baseline">
                <Box>
                    <Typography variant="h3" component="span">
                        {currencyExact(summary.total_outstanding)}
                    </Typography>
                    <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                        total outstanding
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="h3" component="span">
                        {currencyShort(summary.total_invoiced)}
                    </Typography>
                    <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                        total invoiced
                    </Typography>
                </Box>
            </Stack>
            <StackedBar summary={summary} />
            <Stack direction="row" spacing={3}>
                <LegendDot color="success.main" label={`${summary.paid_count} Paid`} />
                <LegendDot color="cement.300" label={`${summary.unpaid_count} Unpaid - Not Overdue`} />
                <LegendDot color="warning.main" label={`${summary.overdue_count} Overdue`} />
            </Stack>
        </Box>
    );
}
